import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Save, Eye, EyeOff, Sparkles, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";

const STRUCT_TYPES = ["BlogPosting", "NewsArticle", "TechArticle", "HowTo"];
const LOCALES = ["en", "it", "es", "fr", "de"];

export default function AdminBlogEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [collapsed, setCollapsed] = useState(false);
    const [post, setPost] = useState(null);
    const [categories, setCategories] = useState([]);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [preview, setPreview] = useState(false);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [p, cats] = await Promise.all([
                    api.adminBlogGet(id),
                    api.blogCategories(),
                ]);
                setPost(p);
                setCategories(cats.items || []);
                setForm({
                    title: p.title || "",
                    subtitle: p.subtitle || "",
                    excerpt: p.excerpt || "",
                    content_markdown: p.content_markdown || "",
                    category_slug: p.category_slug || "product-updates",
                    tags: (p.tags || []).join(", "),
                    meta_title: p.meta_title || "",
                    meta_description: p.meta_description || "",
                    canonical_url: p.canonical_url || "",
                    structured_data_type: p.structured_data_type || "BlogPosting",
                    locale: p.locale || "en",
                    slug: p.slug || "",
                });
            } catch (e) {
                toast.error(`Load failed: ${e.message}`);
                navigate("/admin/blog");
            }
        })();
    }, [id, navigate]);

    if (!post || !form) {
        return (
            <div className="min-h-screen bg-[var(--color-bg)] p-16 text-text-secondary">…</div>
        );
    }

    const patchField = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        setDirty(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            const patch = {
                title: form.title,
                subtitle: form.subtitle,
                excerpt: form.excerpt,
                content_markdown: form.content_markdown,
                category_slug: form.category_slug,
                tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
                meta_title: form.meta_title,
                meta_description: form.meta_description,
                canonical_url: form.canonical_url,
                structured_data_type: form.structured_data_type,
                locale: form.locale,
                slug: form.slug || undefined,
            };
            const resp = await api.adminBlogPatch(id, patch);
            setPost(resp.post);
            toast.success("Saved");
            setDirty(false);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const publish = async () => {
        if (dirty) {
            toast.error("Save your changes first");
            return;
        }
        if (!window.confirm("Publish this post? It will be publicly visible.")) return;
        try {
            await api.adminBlogPublish(id);
            toast.success("Published");
            const fresh = await api.adminBlogGet(id);
            setPost(fresh);
        } catch (e) {
            toast.error(e.message);
        }
    };
    const unpublish = async () => {
        try {
            await api.adminBlogUnpublish(id);
            const fresh = await api.adminBlogGet(id);
            setPost(fresh);
            toast.success("Moved to draft");
        } catch (e) { toast.error(e.message); }
    };
    const remove = async () => {
        if (!window.confirm("Delete post permanently?")) return;
        try {
            await api.adminBlogDelete(id);
            toast.success("Deleted");
            navigate("/admin/blog");
        } catch (e) { toast.error(e.message); }
    };

    const isPublished = post.status === "published";

    return (
        <div className="flex min-h-screen bg-[var(--color-bg)] text-text-main">
            <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
            <div className="flex-1 flex flex-col min-w-0">
                <Header />
                <main className="p-6 sm:p-10 max-w-6xl w-full">
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                            <Link
                                to="/admin/blog"
                                data-testid="admin-blog-edit-back"
                                className="p-2 rounded-lg border border-[var(--color-border)] hover:border-[var(--color-primary)]/50 transition-colors"
                            >
                                <ArrowLeft className="h-4 w-4" />
                            </Link>
                            <div>
                                <p className="text-[10px] uppercase tracking-widest text-text-secondary">
                                    {post.status.toUpperCase()} {post.ai_generated && "· AI-DRAFT"}
                                </p>
                                <h1 className="text-xl font-semibold">Edit post</h1>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                data-testid="admin-blog-edit-preview"
                                onClick={() => setPreview((p) => !p)}
                                className="rounded-full border border-[var(--color-border)] px-4 py-2 text-sm text-text-secondary hover:text-text-main"
                            >
                                {preview ? "Edit" : "Preview"}
                            </button>
                            <button
                                data-testid="admin-blog-edit-save"
                                onClick={save}
                                disabled={saving}
                                className="inline-flex items-center gap-2 rounded-full gradient-bg-cta px-4 py-2 text-sm font-medium text-white glow-primary disabled:opacity-60"
                            >
                                <Save className="h-3.5 w-3.5" />
                                Save
                            </button>
                            {isPublished ? (
                                <button
                                    data-testid="admin-blog-edit-unpublish"
                                    onClick={unpublish}
                                    className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] px-4 py-2 text-sm text-text-secondary hover:text-text-main"
                                >
                                    <EyeOff className="h-3.5 w-3.5" /> Unpublish
                                </button>
                            ) : (
                                <button
                                    data-testid="admin-blog-edit-publish"
                                    onClick={publish}
                                    className="inline-flex items-center gap-2 rounded-full bg-[var(--color-success)]/15 border border-[var(--color-success)]/40 text-[var(--color-success)] px-4 py-2 text-sm font-medium"
                                >
                                    <Eye className="h-3.5 w-3.5" /> Publish
                                </button>
                            )}
                            <button
                                data-testid="admin-blog-edit-delete"
                                onClick={remove}
                                className="inline-flex items-center gap-2 rounded-full border border-[var(--color-alert)]/40 text-[var(--color-alert)] px-3 py-2 text-sm"
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    </div>

                    {post.ai_generated && post.status !== "published" && post.created_at === post.updated_at && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="mb-6 rounded-xl border border-[var(--color-secondary)]/40 bg-[var(--color-secondary)]/5 p-4 flex items-start gap-3"
                        >
                            <Sparkles className="h-4 w-4 text-[var(--color-secondary)] mt-0.5" />
                            <div className="text-sm">
                                <p className="font-medium text-[var(--color-secondary)]">Human review required</p>
                                <p className="text-text-secondary mt-1">
                                    This AI-generated draft has not been edited yet. Save at least once
                                    after your review before publishing.
                                </p>
                            </div>
                        </motion.div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Main */}
                        <div className="lg:col-span-2 space-y-4">
                            <div>
                                <label className="block text-xs uppercase tracking-widest text-text-secondary mb-1.5">Title</label>
                                <input
                                    data-testid="admin-blog-field-title"
                                    value={form.title}
                                    onChange={(e) => patchField("title", e.target.value)}
                                    className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-2.5 text-lg font-semibold outline-none focus:border-[var(--color-primary)]/60"
                                />
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-widest text-text-secondary mb-1.5">Subtitle</label>
                                <input
                                    data-testid="admin-blog-field-subtitle"
                                    value={form.subtitle}
                                    onChange={(e) => patchField("subtitle", e.target.value)}
                                    className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-2.5 text-sm outline-none focus:border-[var(--color-primary)]/60"
                                />
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-widest text-text-secondary mb-1.5">Excerpt</label>
                                <textarea
                                    data-testid="admin-blog-field-excerpt"
                                    rows={3}
                                    value={form.excerpt}
                                    onChange={(e) => patchField("excerpt", e.target.value)}
                                    className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-2.5 text-sm outline-none focus:border-[var(--color-primary)]/60"
                                />
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-widest text-text-secondary mb-1.5">Content (Markdown)</label>
                                {preview ? (
                                    <div
                                        data-testid="admin-blog-field-preview"
                                        className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 prose prose-invert max-w-none min-h-[400px]"
                                    >
                                        <ReactMarkdown>{form.content_markdown || ""}</ReactMarkdown>
                                    </div>
                                ) : (
                                    <textarea
                                        data-testid="admin-blog-field-content"
                                        rows={22}
                                        value={form.content_markdown}
                                        onChange={(e) => patchField("content_markdown", e.target.value)}
                                        className="w-full font-mono rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-3 text-sm outline-none focus:border-[var(--color-primary)]/60"
                                    />
                                )}
                            </div>
                        </div>

                        {/* Meta panel */}
                        <aside className="space-y-4">
                            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 space-y-4">
                                <h3 className="text-xs uppercase tracking-widest text-text-secondary">Publishing</h3>
                                <div>
                                    <label className="block text-xs text-text-secondary mb-1">Slug</label>
                                    <input
                                        data-testid="admin-blog-field-slug"
                                        value={form.slug}
                                        onChange={(e) => patchField("slug", e.target.value)}
                                        className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm outline-none focus:border-[var(--color-primary)]/60"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-text-secondary mb-1">Category</label>
                                    <select
                                        data-testid="admin-blog-field-category"
                                        value={form.category_slug}
                                        onChange={(e) => patchField("category_slug", e.target.value)}
                                        className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm outline-none"
                                    >
                                        {categories.map((c) => (
                                            <option key={c.slug} value={c.slug}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs text-text-secondary mb-1">Tags (comma separated)</label>
                                    <input
                                        data-testid="admin-blog-field-tags"
                                        value={form.tags}
                                        onChange={(e) => patchField("tags", e.target.value)}
                                        className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm outline-none focus:border-[var(--color-primary)]/60"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-text-secondary mb-1">Locale</label>
                                    <select
                                        data-testid="admin-blog-field-locale"
                                        value={form.locale}
                                        onChange={(e) => patchField("locale", e.target.value)}
                                        className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm outline-none"
                                    >
                                        {LOCALES.map((l) => (
                                            <option key={l} value={l}>{l.toUpperCase()}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 space-y-4">
                                <h3 className="text-xs uppercase tracking-widest text-text-secondary">SEO</h3>
                                <div>
                                    <label className="block text-xs text-text-secondary mb-1">Meta title (≤60ch)</label>
                                    <input
                                        data-testid="admin-blog-field-meta-title"
                                        value={form.meta_title}
                                        onChange={(e) => patchField("meta_title", e.target.value)}
                                        maxLength={70}
                                        className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm outline-none focus:border-[var(--color-primary)]/60"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-text-secondary mb-1">Meta description (140-160ch)</label>
                                    <textarea
                                        data-testid="admin-blog-field-meta-description"
                                        rows={3}
                                        value={form.meta_description}
                                        onChange={(e) => patchField("meta_description", e.target.value)}
                                        maxLength={180}
                                        className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm outline-none focus:border-[var(--color-primary)]/60"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-text-secondary mb-1">Canonical URL</label>
                                    <input
                                        data-testid="admin-blog-field-canonical"
                                        value={form.canonical_url}
                                        onChange={(e) => patchField("canonical_url", e.target.value)}
                                        placeholder="https://…"
                                        className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm outline-none focus:border-[var(--color-primary)]/60"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-text-secondary mb-1">Structured data type</label>
                                    <select
                                        data-testid="admin-blog-field-structured"
                                        value={form.structured_data_type}
                                        onChange={(e) => patchField("structured_data_type", e.target.value)}
                                        className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm outline-none"
                                    >
                                        {STRUCT_TYPES.map((s) => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </aside>
                    </div>
                </main>
            </div>
        </div>
    );
}
